import { SettingsIcon } from "@chakra-ui/icons";
import {
  Button,
  Flex,
  Grid,
  Icon,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberInput,
  NumberInputField,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { useAtom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import { Fragment, useCallback, useEffect, useState } from "react";

import { useAccount, useIsAuthenticated } from "@azure/msal-react";
import {
  EnergyPerformanceFeeCategoryModel,
  getEnergyPerformanceFeeCategoryList,
} from "../../clients/KompasApi";
import { usePagination } from "../../shared/hooks/usePagination";

export const gasPriceAtom = atomWithStorage("gasPrice", 0);
export const electricityPriceAtom = atomWithStorage("electricityPrice", 0);
export const extraCostsAtom = atomWithStorage<Record<string, number>>(
  "extraCosts",
  {},
);
export const joulePriceAtom = atomWithStorage("joulePrice", 0);

export function SettingsModal() {
  const isAuthenticated = useIsAuthenticated();
  const account = useAccount();

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [gasPrice, setGasPrice] = useAtom(gasPriceAtom);
  const [electricityPrice, setElectricityPrice] = useAtom(electricityPriceAtom);
  const [joulePrice, setJoulePrice] = useAtom(joulePriceAtom);
  const [extraCosts, setExtraCosts] = useAtom(extraCostsAtom);
  const [tempGasPrice, setTempGasPrice] = useState(gasPrice);
  const [tempElePrice, setTempElePrice] = useState(electricityPrice);
  const [tempExtraCosts, setTempExtraCosts] = useState(extraCosts);
  const [tempJoulePrice, setTempJoulePrice] = useState(joulePrice);

  const { offset, limit } = usePagination("EPVTypes", 20);
  const [epvTypes, setEpvTypes] = useState<EnergyPerformanceFeeCategoryModel[]>(
    [],
  );

  useEffect(() => {
    if (!isAuthenticated || !account) return;

    const abortController = new AbortController();

    getEnergyPerformanceFeeCategoryList({
      query: { Offset: offset, Limit: limit },
      signal: abortController.signal,
      throwOnError: true,
    })
      .then(({ data }) => {
        if (abortController.signal.aborted) return;

        if (data) {
          setEpvTypes((prev) => {
            prev.splice(offset, limit, ...(data.items ?? []));
            return prev.slice(0, offset + limit);
          });
        }
      })
      .catch((e) => {
        if (abortController.signal.aborted) return;
        throw e;
      });

    return () => abortController.abort("Component unmounted or re-rendered");
  }, [isAuthenticated, account, offset, limit]);

  const handleSave = useCallback(() => {
    setGasPrice(tempGasPrice);
    setElectricityPrice(tempElePrice);
    setExtraCosts(tempExtraCosts);
    setJoulePrice(tempJoulePrice);
    onClose();
  }, [
    onClose,
    setElectricityPrice,
    setGasPrice,
    setJoulePrice,
    setExtraCosts,
    tempElePrice,
    tempGasPrice,
    tempJoulePrice,
    tempExtraCosts,
  ]);

  const handleClose = useCallback(() => {
    setTempGasPrice(gasPrice);
    setTempElePrice(electricityPrice);
    setTempJoulePrice(joulePrice);
    setTempExtraCosts(extraCosts);
    onClose();
  }, [electricityPrice, gasPrice, joulePrice, extraCosts, onClose]);

  return (
    <>
      <Icon cursor="pointer" h={6} m={1} onClick={onOpen} w={6}>
        <SettingsIcon />
      </Icon>

      <Modal isOpen={isOpen} onClose={handleClose} size="sm">
        <ModalOverlay />
        <ModalContent borderRadius={8}>
          <ModalHeader>Energiekosten</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Grid alignItems="center" gridRowGap={4}>
              <Flex gap={2}>
                <Text>
                  <Image
                    display="inline-block"
                    h={4}
                    mb={-0.5}
                    mr={1}
                    src="/svg/fire.svg"
                  />
                  Gasprijs
                </Text>
                <Text color="brand.gray.120" fontSize={14} lineHeight="24px">
                  &euro;/m<sup>3</sup>
                </Text>
              </Flex>
              <NumberInput
                defaultValue={tempGasPrice}
                onBlur={(e) => setTempGasPrice(Number(e.target.value))}
              >
                <NumberInputField />
              </NumberInput>
              <Flex gap={2}>
                <Text>
                  <Image
                    display="inline-block"
                    h={4}
                    mb={-0.5}
                    mr={1}
                    src="/svg/lightning.svg"
                  />
                  Stroomprijs
                </Text>
                <Text color="brand.gray.120" fontSize={14} lineHeight="24px">
                  &euro;/kWh
                </Text>
              </Flex>
              <NumberInput
                defaultValue={tempElePrice}
                onBlur={(e) => setTempElePrice(Number(e.target.value))}
              >
                <NumberInputField />
              </NumberInput>
              {epvTypes.map((epvType) => {
                const value = tempExtraCosts[epvType.name] || 0;

                return (
                  <Fragment key={epvType.id}>
                    <Flex gap={2}>
                      <Text>
                        <Image
                          display="inline-block"
                          h={4}
                          mb={-0.5}
                          mr={1}
                          src="/svg/solar.svg"
                        />
                        EPV {epvType.name}
                      </Text>
                      <Text
                        color="brand.gray.120"
                        fontSize={14}
                        lineHeight="24px"
                      >
                        &euro;/m<sup>2</sup>gbo
                      </Text>
                    </Flex>
                    <NumberInput
                      defaultValue={value}
                      onBlur={(e) =>
                        setTempExtraCosts((current) => ({
                          ...current,
                          [epvType.name]: Number(e.target.value),
                        }))
                      }
                    >
                      <NumberInputField />
                    </NumberInput>
                  </Fragment>
                );
              })}
              <Flex gap={2}>
                <Text>
                  <Image
                    display="inline-block"
                    h={4}
                    mb={-0.5}
                    mr={1}
                    src="/svg/fire.svg"
                  />
                  Warmtenet
                </Text>
                <Text color="brand.gray.120" fontSize={14} lineHeight="24px">
                  &euro;/J
                </Text>
              </Flex>
              <NumberInput
                defaultValue={tempJoulePrice}
                onBlur={(e) => setTempJoulePrice(Number(e.target.value))}
              >
                <NumberInputField />
              </NumberInput>
            </Grid>
          </ModalBody>

          <ModalFooter>
            <Button colorScheme="blue" onClick={handleSave}>
              Opslaan
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
